import { createMemo, createSignal, For, Show } from "solid-js";
import { createQuery } from "@tanstack/solid-query";
import { useConfirm } from "../shared/confirm-dialog";

const ROLE_OPTIONS = [
  { id: "support", label: "Support" },
  { id: "admin", label: "Admin" },
  { id: "auditor", label: "Audit" },
  { id: "super_admin", label: "Super admin" },
] as const;

type RoleId = (typeof ROLE_OPTIONS)[number]["id"];

interface Tenant {
  id: string;
  name: string;
  slug: string;
  quotas?: { maxOperators?: number | null };
  usage?: { operators?: number };
}

interface AdminUser {
  id: string;
  provider: string;
  email: string | null;
  name: string | null;
  roles: string[];
  tenantId: string | null;
  disabledAt: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  mustChangePassword: boolean;
}

async function jget<T>(url: string): Promise<T> {
  const r = await fetch(url, { credentials: "include", cache: "no-store" });
  if (!r.ok) throw new Error(`${url} -> ${r.status}`);
  return r.json() as Promise<T>;
}

async function jsend<T = Record<string, unknown>>(url: string, method: string, body?: unknown): Promise<T> {
  const r = await fetch(url, {
    method,
    credentials: "include",
    headers: body === undefined ? undefined : { "content-type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = (await r.json().catch(() => ({}))) as T & { error?: string };
  if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
  return data;
}

function errorMessage(error: unknown): string | null {
  if (!error) return null;
  return error instanceof Error ? error.message : String(error);
}

function fmtDate(value: string | null | undefined) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString();
}

function roleLabel(role: string) {
  return ROLE_OPTIONS.find((r) => r.id === role)?.label || role;
}

export function SuperAdminUsersPage() {
  const { confirm } = useConfirm();
  const [err, setErr] = createSignal<string | null>(null);
  const [notice, setNotice] = createSignal<string | null>(null);
  const [busyId, setBusyId] = createSignal<string | null>(null);
  const [tempPassword, setTempPassword] = createSignal<{ email: string; password: string } | null>(null);

  const [search, setSearch] = createSignal("");
  const [roleFilter, setRoleFilter] = createSignal("");
  const [disabledFilter, setDisabledFilter] = createSignal("");
  const [tenantFilter, setTenantFilter] = createSignal("");

  const [newName, setNewName] = createSignal("");
  const [newEmail, setNewEmail] = createSignal("");
  const [newPassword, setNewPassword] = createSignal("");
  const [newTenantId, setNewTenantId] = createSignal("");
  const [newMustChange, setNewMustChange] = createSignal(true);
  const [newRoles, setNewRoles] = createSignal<string[]>(["support"]);

  const usersQ = createQuery<{ users: AdminUser[] }>(() => ({
    queryKey: ["super-admin-users", search().trim(), roleFilter(), disabledFilter()],
    queryFn: () => {
      const q = new URLSearchParams();
      const s = search().trim();
      if (s) q.set("search", s);
      if (roleFilter()) q.set("role", roleFilter());
      if (disabledFilter()) q.set("disabled", disabledFilter());
      return jget(`/auth/admin/users${q.toString() ? `?${q}` : ""}`);
    },
  }));

  const tenantsQ = createQuery<{ ok: boolean; tenants: Tenant[] }>(() => ({
    queryKey: ["super-admin-users-tenants"],
    queryFn: () => jget("/api/super-admin/tenants"),
  }));

  const tenants = () => tenantsQ.data?.tenants || [];
  const users = () => usersQ.data?.users || [];
  const visibleUsers = createMemo(() => {
    const tenant = tenantFilter();
    if (!tenant) return users();
    if (tenant === "__none__") return users().filter((u) => !u.tenantId);
    return users().filter((u) => u.tenantId === tenant);
  });
  const activeUsers = createMemo(() => users().filter((u) => !u.disabledAt).length);
  const tenantUsers = createMemo(() => users().filter((u) => u.tenantId).length);
  const loadError = () => errorMessage(usersQ.error) || errorMessage(tenantsQ.error);

  function tenantName(id: string | null | undefined) {
    if (!id) return "Plateforme";
    return tenants().find((t) => t.id === id)?.name || id.slice(0, 8);
  }

  function setRoleForCreate(role: RoleId, checked: boolean) {
    const next = checked ? [...newRoles(), role] : newRoles().filter((r) => r !== role);
    setNewRoles(Array.from(new Set(next)));
  }

  async function refetchAll() {
    await Promise.all([usersQ.refetch(), tenantsQ.refetch()]);
  }

  async function patchUser(user: AdminUser, body: Record<string, unknown>) {
    setErr(null);
    setNotice(null);
    setBusyId(user.id);
    try {
      const data = await jsend<{ user?: AdminUser }>(`/auth/admin/users/${user.id}`, "PATCH", body);
      await refetchAll();
      setNotice(`${data.user?.email || user.email || "Compte"} mis à jour.`);
      return true;
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      return false;
    } finally {
      setBusyId(null);
    }
  }

  async function updateRoles(user: AdminUser, role: RoleId, checked: boolean) {
    const current = Array.isArray(user.roles) ? user.roles : [];
    const next = checked ? [...current, role] : current.filter((r) => r !== role);
    const clean = Array.from(new Set(next));
    if (clean.length === 0) {
      setErr("Un compte doit garder au moins un rôle.");
      return;
    }
    await patchUser(user, { roles: clean });
  }

  async function createUser() {
    const name = newName().trim();
    const email = newEmail().trim().toLowerCase();
    const password = newPassword();
    if (!name || !email || password.length < 8) {
      setErr("Nom, email et mot de passe de 8 caractères minimum requis.");
      return;
    }
    if (newRoles().length === 0) {
      setErr("Sélectionnez au moins un rôle.");
      return;
    }
    setErr(null);
    setNotice(null);
    try {
      await jsend("/auth/admin/users", "POST", {
        name,
        email,
        password,
        roles: newRoles(),
        tenantId: newTenantId() || null,
        mustChangePassword: newMustChange(),
      });
      setNewName("");
      setNewEmail("");
      setNewPassword("");
      setNewTenantId("");
      setNewRoles(["support"]);
      setNewMustChange(true);
      setNotice(`Compte ${email} créé.`);
      await refetchAll();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  async function resetPassword(user: AdminUser) {
    const ok = await confirm({
      level: "warn",
      title: "Générer un mot de passe temporaire ?",
      body: "Le compte devra changer ce mot de passe à la prochaine connexion.",
      confirmLabel: "Générer",
    });
    if (!ok) return;
    setErr(null);
    setNotice(null);
    setBusyId(user.id);
    try {
      const data = await jsend<{ password?: string }>(`/auth/admin/users/${user.id}/reset-password`, "POST", {
        generate: true,
      });
      setTempPassword({ email: user.email || user.id, password: data.password || "" });
      await refetchAll();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  }

  async function disableOrEnable(user: AdminUser) {
    const disable = !user.disabledAt;
    if (disable) {
      const ok = await confirm({
        level: "warn",
        title: "Désactiver ce compte ?",
        body: "Le compte ne pourra plus se connecter tant qu'il n'est pas réactivé.",
        confirmLabel: "Désactiver",
      });
      if (!ok) return;
    }
    await patchUser(user, { disabled: disable });
  }

  async function deleteUser(user: AdminUser) {
    const label = user.email || user.name || user.id;
    const ok = await confirm({
      level: "danger",
      title: "Supprimer ce compte ?",
      body: "La suppression est logique : le compte sera désactivé et conservé pour l'audit.",
      entityName: label,
      confirmLabel: "Supprimer",
    });
    if (!ok) return;
    setErr(null);
    setNotice(null);
    setBusyId(user.id);
    try {
      await jsend(`/auth/admin/users/${user.id}`, "DELETE");
      await refetchAll();
      setNotice(`Compte ${label} supprimé.`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  }

  const inputStyle = {
    background: "var(--bg-2, #1e293b)",
    color: "var(--text-1, #e8ecf3)",
    border: "1px solid var(--border, #334155)",
    "border-radius": "4px",
    padding: "5px 8px",
    "font-size": "12px",
    "min-height": "28px",
  } as const;

  const compactCell = { "font-size": "11px", color: "var(--text-3)" } as const;

  return (
    <div class="plugins-console" style={{ overflow: "auto" }}>
      <div class="plugins-console-header">
        <span class="plugins-console-title">
          Utilisateurs
          <span class="plugins-console-sub">comptes opérateurs + droits tenants</span>
        </span>
      </div>

      <Show when={err() || loadError()}>
        <div
          style={{
            background: "rgba(220,38,38,0.12)",
            border: "1px solid var(--danger, #dc2626)",
            color: "var(--danger, #f87171)",
            padding: "6px 10px",
            "border-radius": "4px",
            "font-size": "12px",
            "margin-bottom": "10px",
          }}
        >
          {err() || loadError()}
        </div>
      </Show>

      <Show when={notice()}>
        <div
          style={{
            background: "rgba(34,197,94,0.10)",
            border: "1px solid rgba(34,197,94,0.45)",
            color: "#86efac",
            padding: "6px 10px",
            "border-radius": "4px",
            "font-size": "12px",
            "margin-bottom": "10px",
          }}
        >
          {notice()}
        </div>
      </Show>

      <Show when={tempPassword()}>
        {(p) => (
          <div
            style={{
              display: "flex",
              gap: "8px",
              "align-items": "center",
              "flex-wrap": "wrap",
              background: "rgba(245,158,11,0.12)",
              border: "1px solid rgba(245,158,11,0.5)",
              color: "#fde68a",
              padding: "8px 10px",
              "border-radius": "4px",
              "font-size": "12px",
              "margin-bottom": "10px",
            }}
          >
            <strong>Mot de passe temporaire pour {p().email}</strong>
            <code style={{ color: "#fff", "font-size": "13px" }}>{p().password}</code>
            <button class="btn btn--xs" type="button" onClick={() => navigator.clipboard?.writeText(p().password)}>
              Copier
            </button>
            <button class="btn btn--xs" type="button" onClick={() => setTempPassword(null)}>
              Masquer
            </button>
          </div>
        )}
      </Show>

      <div
        style={{
          display: "grid",
          "grid-template-columns": "repeat(3, minmax(0, 1fr))",
          flex: "0 0 auto",
          gap: "8px",
          "margin-bottom": "10px",
        }}
      >
        <div style={{ border: "1px solid var(--border)", "border-radius": "6px", padding: "8px" }}>
          <div style={{ color: "var(--text-4)", "font-size": "11px" }}>Comptes actifs</div>
          <div style={{ color: "var(--text-1)", "font-size": "20px", "font-weight": 650 }}>{activeUsers()}</div>
        </div>
        <div style={{ border: "1px solid var(--border)", "border-radius": "6px", padding: "8px" }}>
          <div style={{ color: "var(--text-4)", "font-size": "11px" }}>Assignés à un tenant</div>
          <div style={{ color: "var(--text-1)", "font-size": "20px", "font-weight": 650 }}>{tenantUsers()}</div>
        </div>
        <div style={{ border: "1px solid var(--border)", "border-radius": "6px", padding: "8px" }}>
          <div style={{ color: "var(--text-4)", "font-size": "11px" }}>Tenants disponibles</div>
          <div style={{ color: "var(--text-1)", "font-size": "20px", "font-weight": 650 }}>{tenants().length}</div>
        </div>
      </div>

      <div class="rmm-table-wrap" style={{ flex: "0 0 auto", "margin-bottom": "14px" }}>
        <div style={{ display: "flex", gap: "6px", padding: "8px", "flex-wrap": "wrap", "align-items": "center" }}>
          <input
            style={inputStyle}
            placeholder="Nom"
            value={newName()}
            onInput={(e) => setNewName(e.currentTarget.value)}
          />
          <input
            style={inputStyle}
            type="email"
            placeholder="email@client.fr"
            value={newEmail()}
            onInput={(e) => setNewEmail(e.currentTarget.value)}
          />
          <input
            style={inputStyle}
            type="password"
            placeholder="Mot de passe temporaire"
            value={newPassword()}
            onInput={(e) => setNewPassword(e.currentTarget.value)}
          />
          <select style={inputStyle} value={newTenantId()} onChange={(e) => setNewTenantId(e.currentTarget.value)}>
            <option value="">Plateforme (aucun tenant)</option>
            <For each={tenants()}>{(t) => <option value={t.id}>{t.name}</option>}</For>
          </select>
          <div style={{ display: "flex", gap: "6px", "align-items": "center", "flex-wrap": "wrap" }}>
            <For each={ROLE_OPTIONS}>
              {(role) => (
                <label style={{ display: "inline-flex", gap: "4px", "align-items": "center", ...compactCell }}>
                  <input
                    type="checkbox"
                    checked={newRoles().includes(role.id)}
                    onChange={(e) => setRoleForCreate(role.id, e.currentTarget.checked)}
                  />
                  {role.label}
                </label>
              )}
            </For>
          </div>
          <label style={{ display: "inline-flex", gap: "4px", "align-items": "center", ...compactCell }}>
            <input
              type="checkbox"
              checked={newMustChange()}
              onChange={(e) => setNewMustChange(e.currentTarget.checked)}
            />
            Changer au login
          </label>
          <button class="btn btn--primary btn--xs" type="button" onClick={createUser}>
            + Créer
          </button>
        </div>
      </div>

      <div class="rmm-table-wrap" style={{ "margin-bottom": "14px" }}>
        <div style={{ display: "flex", gap: "6px", padding: "8px", "flex-wrap": "wrap", "align-items": "center" }}>
          <input
            style={{ ...inputStyle, width: "220px" }}
            placeholder="Rechercher nom ou email"
            value={search()}
            onInput={(e) => setSearch(e.currentTarget.value)}
          />
          <select style={inputStyle} value={roleFilter()} onChange={(e) => setRoleFilter(e.currentTarget.value)}>
            <option value="">Tous les rôles</option>
            <For each={ROLE_OPTIONS}>{(r) => <option value={r.id}>{r.label}</option>}</For>
          </select>
          <select
            style={inputStyle}
            value={disabledFilter()}
            onChange={(e) => setDisabledFilter(e.currentTarget.value)}
          >
            <option value="">Tous statuts</option>
            <option value="false">Actifs</option>
            <option value="true">Désactivés</option>
          </select>
          <select style={inputStyle} value={tenantFilter()} onChange={(e) => setTenantFilter(e.currentTarget.value)}>
            <option value="">Tous tenants</option>
            <option value="__none__">Plateforme</option>
            <For each={tenants()}>{(t) => <option value={t.id}>{t.name}</option>}</For>
          </select>
          <button
            class="btn btn--xs"
            type="button"
            onClick={() => {
              setSearch("");
              setRoleFilter("");
              setDisabledFilter("");
              setTenantFilter("");
            }}
          >
            Réinitialiser
          </button>
          <button class="btn btn--xs" type="button" onClick={() => void refetchAll()}>
            Rafraîchir
          </button>
        </div>

        <table class="rmm-table">
          <thead>
            <tr>
              <th>Compte</th>
              <th>Tenant</th>
              <th>Rôles</th>
              <th>Sécurité</th>
              <th>Statut</th>
              <th>Dernière connexion</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <Show
              when={!usersQ.isLoading && !tenantsQ.isLoading}
              fallback={
                <tr>
                  <td colSpan={7}>Chargement...</td>
                </tr>
              }
            >
              <For each={visibleUsers()}>
                {(u) => (
                  <tr data-selected={busyId() === u.id ? "true" : undefined}>
                    <td>
                      <div style={{ color: "var(--text-1)", "font-weight": 600 }}>{u.name || u.email || u.id}</div>
                      <div style={{ color: "var(--text-4)", "font-size": "11px" }}>{u.email || "-"}</div>
                    </td>
                    <td>
                      <select
                        style={{ ...inputStyle, width: "180px" }}
                        value={u.tenantId || ""}
                        disabled={busyId() === u.id}
                        title={`Tenant actuel : ${tenantName(u.tenantId)}`}
                        onChange={(e) => void patchUser(u, { tenantId: e.currentTarget.value || null })}
                      >
                        <option value="">Plateforme</option>
                        <For each={tenants()}>{(t) => <option value={t.id}>{t.name}</option>}</For>
                      </select>
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: "6px", "flex-wrap": "wrap" }}>
                        <For each={ROLE_OPTIONS}>
                          {(role) => (
                            <label
                              style={{ display: "inline-flex", gap: "4px", "align-items": "center", ...compactCell }}
                            >
                              <input
                                type="checkbox"
                                checked={u.roles.includes(role.id)}
                                disabled={busyId() === u.id}
                                onChange={(e) => void updateRoles(u, role.id, e.currentTarget.checked)}
                              />
                              {roleLabel(role.id)}
                            </label>
                          )}
                        </For>
                      </div>
                    </td>
                    <td style={compactCell}>
                      <label style={{ display: "inline-flex", gap: "4px", "align-items": "center" }}>
                        <input
                          type="checkbox"
                          checked={u.mustChangePassword}
                          disabled={busyId() === u.id}
                          onChange={(e) => void patchUser(u, { mustChangePassword: e.currentTarget.checked })}
                        />
                        Changer au login
                      </label>
                      <div>Provider: {u.provider || "local"}</div>
                    </td>
                    <td style={compactCell}>{u.disabledAt ? "Désactivé" : "Actif"}</td>
                    <td style={compactCell}>{fmtDate(u.lastLoginAt)}</td>
                    <td>
                      <div style={{ display: "flex", gap: "4px", "flex-wrap": "wrap" }}>
                        <button
                          class="btn btn--xs"
                          type="button"
                          disabled={busyId() === u.id || u.provider !== "local"}
                          onClick={() => void resetPassword(u)}
                        >
                          Reset MDP
                        </button>
                        <button
                          class={`btn btn--xs ${u.disabledAt ? "" : "btn--warn"}`}
                          type="button"
                          disabled={busyId() === u.id}
                          onClick={() => void disableOrEnable(u)}
                        >
                          {u.disabledAt ? "Réactiver" : "Désactiver"}
                        </button>
                        <button
                          class="btn btn--xs btn--danger"
                          type="button"
                          disabled={busyId() === u.id}
                          onClick={() => void deleteUser(u)}
                        >
                          Supprimer
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </For>
              <Show when={!usersQ.error && visibleUsers().length === 0}>
                <tr>
                  <td colSpan={7} style={{ color: "var(--text-4)", "text-align": "center", padding: "14px" }}>
                    Aucun compte ne correspond aux filtres.
                  </td>
                </tr>
              </Show>
            </Show>
          </tbody>
        </table>
      </div>
    </div>
  );
}
